import {
  type KilnCoefficientsResponse,
  type QuarterlyForecastKilnCoefficientHierarchy,
  type MtmYtmRequestPayload,
  formatDateUtcToTimezoneDateOnly,
  PERFORMANCE_KPI_REQUEST_STALE_TIME
} from '@domain/kpis'
import {ApiClient} from '@hconnect/apiclient'
import {Iso8601} from '@hconnect/uikit'
import {useQuery, UseQueryOptions} from '@tanstack/react-query'
import {AxiosError} from 'axios'

const getKilnCoefficientMtmYtmUrlSuffix = (showYTM?: boolean) =>
  showYTM ? 'kiln-coefficients-ytm' : 'kiln-coefficients-mtm'

export const getKilnCoefficientMtmYtmQueryKeys = (requestPayload: MtmYtmRequestPayload) => [
  getKilnCoefficientMtmYtmUrlSuffix(requestPayload.showYTM),
  formatDateUtcToTimezoneDateOnly(requestPayload.timestamp, requestPayload.timezone),
  requestPayload.locationId
]

export const getKilnCoefficients = async (
  {timestamp, timezone, locationId, showYTM}: MtmYtmRequestPayload,
  signal?: AbortSignal
) => {
  const referenceDate = formatDateUtcToTimezoneDateOnly(timestamp, timezone)

  const url = `/kpi/dashboard/${getKilnCoefficientMtmYtmUrlSuffix(showYTM)}`
  const response = await ApiClient.axiosInstance.get<KilnCoefficientsResponse>(url, {
    params: {locationId, date: referenceDate},
    signal
  })

  return response.data
}

export const useKilnCoefficients = <T>(
  payload: MtmYtmRequestPayload,
  options?: Omit<UseQueryOptions<KilnCoefficientsResponse, AxiosError, T>, 'queryKey' | 'queryFn'>
) => {
  const QueryKeys = getKilnCoefficientMtmYtmQueryKeys(payload)

  return useQuery<KilnCoefficientsResponse, AxiosError, T>({
    queryKey: QueryKeys,
    queryFn: async ({signal}) => {
      return getKilnCoefficients(payload, signal)
    },
    staleTime: PERFORMANCE_KPI_REQUEST_STALE_TIME,
    ...options
  })
}

// used in future when data is available HCP-68646
export const useKilnCoefficientsQuarterly = (
  timestamp: Iso8601,
  timezone: string,
  locationId?: string,
  options?: Omit<
    UseQueryOptions<QuarterlyForecastKilnCoefficientHierarchy | null, AxiosError>,
    'queryKey' | 'queryFn'
  >
) => {
  const referenceDate = formatDateUtcToTimezoneDateOnly(timestamp, timezone)

  return useQuery<QuarterlyForecastKilnCoefficientHierarchy | null, AxiosError>({
    queryKey: ['kiln-coefficients-quarterly', referenceDate, locationId],
    queryFn: async ({signal}) => {
      if (!locationId) return Promise.resolve(null)

      const response = await ApiClient.axiosInstance.get<QuarterlyForecastKilnCoefficientHierarchy>(
        '/kpi/dashboard/kiln-coefficients-qf',
        {
          params: {locationId, timestamp: referenceDate},
          signal
        }
      )
      return response.data
    },
    staleTime: PERFORMANCE_KPI_REQUEST_STALE_TIME,
    ...options
  })
}
